import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, ShieldCheck, MapPin, HeartPulse, Ambulance, BadgeCheck, Star, Stethoscope, Timer, Activity } from "lucide-react";
import SITE from "@/lib/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — Split layout */}
      <section className="relative overflow-hidden bg-[#FBFBF9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#EDF1ED] px-3 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-[#1A3A32]">
                <span className="relative inline-flex h-2 w-2">
                  <span className="ambuly-siren-pulse absolute inset-0" />
                  <span className="relative inline-block h-2 w-2 rounded-full bg-[#E5383B]" />
                </span>
                24 / 7 Emergency Response
              </div>
              <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] font-bold text-[#14241F] ambuly-fade-up">
                Ambulances,
                <br />
                <span className="text-[#1A3A32]">when every</span>
                <br />
                <span className="relative inline-block">
                  <span>second counts.</span>
                  <span className="absolute -bottom-2 left-0 h-1.5 w-24 bg-[#E5383B] rounded-full" />
                </span>
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-[#495D56] max-w-xl">
                Ambuly connects you to verified ambulances, trained paramedics
                and hospitals in minutes. Transparent pricing. Real-time
                tracking. Zero hassle.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-book-btn"
                  className="inline-flex items-center justify-center bg-[#E5383B] text-white hover:bg-[#BA1826] active:bg-[#A4161A] rounded-lg px-8 py-4 font-bold text-lg transition-colors shadow-lg shadow-red-500/20"
                >
                  Book Ambulance <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <a
                  href={`tel:${SITE.legal.emergencyLine}`}
                  data-testid="hero-call-btn"
                  className="inline-flex items-center justify-center border-2 border-[#1A3A32] text-[#1A3A32] hover:bg-[#EDF1ED] rounded-lg px-6 py-3.5 font-semibold"
                >
                  <PhoneCall className="mr-2 h-4 w-4" /> Call {SITE.legal.emergencyLine}
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#495D56]">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1A3A32]" /> Verified operators</div>
                <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-[#1A3A32]" /> Trained paramedics</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#1A3A32]" /> West Bengal &amp; beyond</div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-3 bg-[#1A3A32] rounded-3xl -rotate-2 hidden sm:block" aria-hidden />
                <div className="relative rounded-3xl overflow-hidden border border-[#D5DDD7] shadow-xl">
                  <img
                    src={SITE.media.hero}
                    alt="Ambuly ambulance parked outside a hospital at night"
                    className="w-full h-[520px] object-cover"
                    loading="eager"
                  />
                  <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-semibold text-[#14241F]">
                      <span className="inline-block h-2 w-2 rounded-full bg-[#E5383B] animate-pulse" />
                      Live dispatch
                    </span>
                    <span className="rounded-full bg-[#14241F] text-white px-3 py-1.5 text-xs font-semibold">24/7</span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-xl p-4 border border-white/60">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#495D56]">Service</p>
                        <p className="text-sm font-semibold text-[#14241F] mt-0.5">Basic · Advanced · ICU Ambulances</p>
                      </div>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDF1ED] text-[#1A3A32] text-xs font-semibold px-3 py-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#1A3A32]" /> Available
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">What we do</p>
              <h2 className="mt-3 text-4xl sm:text-5xl font-[Outfit] font-semibold text-[#14241F] max-w-2xl">
                One app. Every medical transport need.
              </h2>
            </div>
            <Link to="/services" data-testid="home-services-link" className="inline-flex items-center text-[#1A3A32] font-semibold hover:text-[#E5383B]">
              See all services <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Ambulance, title: "Emergency Response", desc: "BLS & ALS-equipped ambulances dispatched in minutes for critical situations.", tag: "Priority" },
              { icon: Stethoscope, title: "ICU Ambulance", desc: "Ventilators, cardiac monitors and onboard paramedic for critical patients.", tag: "Critical care" },
              { icon: Activity, title: "Non-Emergency Transport", desc: "Scheduled transfers for dialysis, check-ups, discharge & inter-hospital moves.", tag: "Scheduled" },
              { icon: HeartPulse, title: "Neonatal & Pediatric", desc: "Specialised equipment and trained teams for newborns and children.", tag: "Specialised" },
              { icon: Timer, title: "Event Medical Cover", desc: "Standby ambulances for events, film sets, marathons and corporate sites.", tag: "On-site" },
              { icon: BadgeCheck, title: "Corporate & Insurance", desc: "Coverage plans for employees and insurance-partnered dispatch.", tag: "Plans" },
            ].map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group relative bg-white border border-[#D5DDD7] rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32] group-hover:bg-[#1A3A32] group-hover:text-white transition-colors">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#495D56] bg-[#EDF1ED] px-2.5 py-1 rounded-full">{s.tag}</span>
                </div>
                <h3 className="mt-6 text-xl font-[Outfit] font-semibold text-[#14241F]">{s.title}</h3>
                <p className="mt-2 text-[#495D56]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28 bg-[#14241F] text-[#FBFBF9] relative overflow-hidden">
        <div className="absolute inset-0 ambuly-grain opacity-40 pointer-events-none" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E5383B]">How Ambuly works</p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-[Outfit] font-semibold">
              Three taps between you and care.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", title: "Request", desc: "Open the app or call, share your location and patient details." },
              { n: "02", title: "Dispatch", desc: "We assign the nearest verified ambulance — you see the ETA live." },
              { n: "03", title: "Care", desc: "Trained paramedics handle care en route to your preferred hospital." },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl border border-white/10 bg-white/5 p-8 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="font-[Outfit] text-5xl font-bold text-[#E5383B]">{step.n}</span>
                  <span className="h-px flex-1 bg-white/20" />
                </div>
                <h3 className="mt-6 text-2xl font-[Outfit] font-semibold">{step.title}</h3>
                <p className="mt-2 text-[#D5DDD7]/80">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY AMBULY — bento */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">Why Ambuly</p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-[Outfit] font-semibold text-[#14241F]">Built to be trusted in the hardest moment.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <div className="md:col-span-4 rounded-2xl overflow-hidden border border-[#D5DDD7] relative">
              <img src={SITE.media.professional} alt="Paramedic with patient" className="w-full h-72 object-cover" loading="lazy" />
              <div className="p-8">
                <h3 className="text-2xl font-[Outfit] font-semibold text-[#14241F]">Trained, verified paramedics</h3>
                <p className="mt-2 text-[#495D56]">Every partner crew is background-checked and regularly audited for
                  protocol compliance, equipment readiness and clinical skills.</p>
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl bg-[#EDF1ED] p-8 flex flex-col justify-between">
              <ShieldCheck className="h-10 w-10 text-[#1A3A32]" />
              <div>
                <h3 className="text-xl font-[Outfit] font-semibold text-[#14241F]">HIPAA-grade privacy</h3>
                <p className="mt-2 text-[#495D56]">We encrypt your health data at rest and in transit. Always.</p>
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl bg-[#1A3A32] text-white p-8 flex flex-col justify-between">
              <Timer className="h-10 w-10 text-[#E5383B]" />
              <div>
                <h3 className="text-xl font-[Outfit] font-semibold">Real-time ETA</h3>
                <p className="mt-2 text-[#D5DDD7]/80">Watch the ambulance reach you on a live map. No guessing.</p>
              </div>
            </div>
            <div className="md:col-span-4 rounded-2xl border border-[#D5DDD7] bg-white p-8">
              <div className="flex items-start gap-4">
                <Star className="h-10 w-10 text-[#F59E0B]" />
                <div>
                  <h3 className="text-2xl font-[Outfit] font-semibold text-[#14241F]">Transparent, upfront pricing</h3>
                  <p className="mt-2 text-[#495D56]">No surprise fees. You see the exact fare before you book, with itemised billing on every receipt.</p>
                  <Link to="/pricing" data-testid="home-view-pricing" className="mt-4 inline-flex items-center text-[#1A3A32] font-semibold hover:text-[#E5383B]">
                    View pricing <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-20 sm:py-28 bg-[#EDF1ED]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">Our promise</p>
          <h2 className="mt-4 text-3xl sm:text-5xl font-[Outfit] font-semibold text-[#14241F] leading-tight">
            Calm, coordinated care — from the first call to the hospital door.
          </h2>
          <p className="mt-6 text-lg text-[#495D56] max-w-3xl mx-auto">
            Ambuly is being built to make emergency and scheduled ambulance
            services dependable, transparent and accessible. If you'd like to be
            among our first users, hospital or operator partners, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-[#14241F] text-white p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#E5383B]/20 blur-3xl" aria-hidden />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <h2 className="text-4xl sm:text-5xl font-[Outfit] font-semibold tracking-tight">
                  Prepare before you need us.
                </h2>
                <p className="mt-4 text-[#D5DDD7]/80 max-w-2xl text-lg">
                  Save Ambuly on your phone today. Because in an emergency, the
                  fastest click is the one you already know where to find.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                <Link to="/contact" data-testid="cta-book-now" className="inline-flex items-center justify-center bg-[#E5383B] text-white hover:bg-[#BA1826] rounded-lg px-8 py-4 font-bold text-lg">
                  Book now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/services" data-testid="cta-learn-more" className="inline-flex items-center justify-center border-2 border-white/30 text-white hover:bg-white/10 rounded-lg px-6 py-3.5 font-semibold">
                  Explore services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
